import type { CompRef } from '@wix/document-services-types';
import { DISPLAY_GROUPS } from '@wix/component-protocol/schema';
import { AbstractComponentsPlatformService } from '../AbstractComponentsPlatformService';
import { ManifestResolver, VirtualManifestAPI } from '../../api';
import { ExternalPropsHandler } from '../../handlers/ExternalPropsHandler';
import {
  getArrayItemsCacheId,
  getCachedArrayItemIndex,
  setCachedArrayItemIndex,
  deleteCachedArrayItemIndex,
  getAllCachedArrayItemEntries,
} from './handlers';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class ArrayItemsPlatformService extends AbstractComponentsPlatformService {
  #propsHandler: ExternalPropsHandler;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.#propsHandler =
      this.virtualElementProvider.getManifestQueryAPI().props;
  }

  #findArrayItemsSelectedIndexKey(
    resolver: ManifestResolver,
    arrayItemsDisplayGroupKey?: string,
  ) {
    const arrayItemsDisplayGroups = Object.entries(
      resolver.getDisplayGroupDefinitions({
        filter: {
          groupType: DISPLAY_GROUPS.GROUP_TYPE.arrayItems,
        },
      }),
    )
      .filter(([displayGroupKey, _displayGroupValue]) =>
        arrayItemsDisplayGroupKey
          ? displayGroupKey === arrayItemsDisplayGroupKey
          : true,
      )
      .map(([_displayGroupKey, displayGroupValue]) => displayGroupValue);

    if (!arrayItemsDisplayGroups.length || arrayItemsDisplayGroups.length > 1) {
      return undefined;
    }

    return arrayItemsDisplayGroups[0].arrayItems?.selectedItemIndex;
  }

  getArrayItemsSelectedIndex(
    apiOrEditorPointers:
      | VirtualManifestAPI
      | EditorPointer<CompRef>
      | EditorPointer<CompRef>[],
    arrayItemsDisplayGroupKey?: string,
  ) {
    const api =
      apiOrEditorPointers instanceof VirtualManifestAPI
        ? apiOrEditorPointers
        : this.extractVirtualElement(apiOrEditorPointers).getAPI();

    if (api.isMultipleComponentsRoot()) {
      return null;
    }

    const editorPointer = api.getEditorPointer();
    const cachedIndex = getCachedArrayItemIndex(
      this.editorPointerAPI,
      editorPointer,
    );

    if (cachedIndex !== null) {
      return cachedIndex;
    }

    const indexKey = this.#findArrayItemsSelectedIndexKey(
      api.resolver,
      arrayItemsDisplayGroupKey,
    );

    if (!indexKey) {
      return null;
    }

    const data = api.query.getData();

    const defaultSelectedArrayIndex =
      api.resolver.getDefaultDataValue(indexKey);

    const selectedArrayIndex = data[indexKey] ?? defaultSelectedArrayIndex;

    return selectedArrayIndex as number;
  }

  setArrayItemsSelectedIndex(
    apiOrEditorPointers:
      | VirtualManifestAPI
      | EditorPointer<CompRef>
      | EditorPointer<CompRef>[],
    index: number,
    arrayItemsDisplayGroupKey?: string,
  ) {
    const api =
      apiOrEditorPointers instanceof VirtualManifestAPI
        ? apiOrEditorPointers
        : this.extractVirtualElement(apiOrEditorPointers).getAPI();

    if (api.isMultipleComponentsRoot()) {
      return;
    }

    if (typeof index !== 'number') {
      return;
    }

    const indexKey = this.#findArrayItemsSelectedIndexKey(
      api.resolver,
      arrayItemsDisplayGroupKey,
    );

    if (!indexKey) {
      return;
    }

    const editorPointer = api.getEditorPointer();
    setCachedArrayItemIndex(this.editorPointerAPI, editorPointer, index);

    const props = {
      [indexKey]: index,
    };
    this.#propsHandler.updatePropsOverrides(
      editorPointer,
      api.getExtensionType(),
      props,
    );
  }

  resetAllArrayItemsSelectedIndexes() {
    getAllCachedArrayItemEntries().forEach((entry) => {
      this.resetArrayItemsSelectedIndex(entry.editorPointer);
    });
  }

  resetArrayItemsSelectedIndex(
    apiOrEditorPointers:
      | VirtualManifestAPI
      | EditorPointer<CompRef>
      | EditorPointer<CompRef>[],
    arrayItemsDisplayGroupKey?: string,
  ) {
    const api =
      apiOrEditorPointers instanceof VirtualManifestAPI
        ? apiOrEditorPointers
        : this.extractVirtualElement(apiOrEditorPointers).getAPI();

    if (api.isMultipleComponentsRoot()) {
      return;
    }

    const indexKey = this.#findArrayItemsSelectedIndexKey(
      api.resolver,
      arrayItemsDisplayGroupKey,
    );

    if (!indexKey) {
      return;
    }

    const editorPointer = api.getEditorPointer();
    deleteCachedArrayItemIndex(this.editorPointerAPI, editorPointer);

    const selectedArrayIndex = this.getArrayItemsSelectedIndex(
      api,
      arrayItemsDisplayGroupKey,
    );
    const props = {
      [indexKey]: selectedArrayIndex,
    };

    this.#propsHandler.updatePropsOverrides(
      editorPointer,
      api.getExtensionType(),
      props,
    );
  }
}
